import { useSiteStore } from "../stores/siteStore";
import { gateVisible, type Gate, type GateContext } from "./gating";
import { NAV_SECTIONS } from "./navConfig";

/**
 * Routes are gated by the SAME rule as the menu (ADR 395). The nav used to be the only
 * gate: a page hidden from the menu still opened when its address was typed. A route's
 * gate is the nav item it belongs to — the longest nav path that is the URL or a parent
 * of it (`/inventory/runs/x` → `/inventory`, `/admin/warehouses/w/model` →
 * `/admin/warehouses`) — so a new page is gated the moment it is added to the menu, and
 * there is still one gate, not two.
 */

/** Routes that exist only as redirects onto a gated page, or have no menu entry. */
const ROUTE_ALIASES: Record<string, string> = {
  "/admin/calibration": "/admin/warehouses",
  "/admin/mapping": "/admin/warehouses",
};

const NAV_ITEMS = NAV_SECTIONS.flatMap((s) => s.items);

export function gateForPath(pathname: string): Gate | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const target = ROUTE_ALIASES[path] ?? path;
  let best: (Gate & { to: string }) | null = null;
  for (const item of NAV_ITEMS) {
    if (item.to === "/") continue; // home and unknown URLs resolve to the state-aware landing
    if (target === item.to || target.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }
  return best;
}

export function routeAllowed(pathname: string, ctx: GateContext): boolean {
  const gate = gateForPath(pathname);
  return !gate || gateVisible(gate, ctx);
}

/** The gate context of the signed-in user on the active site — shared by the menu and the routes. */
export function useGateContext(): GateContext {
  const isGlobalAdmin = useSiteStore((s) => s.isGlobalAdmin);
  const isSales = useSiteStore((s) => s.isSales);
  const roleRank = useSiteStore((s) => s.roleRank);
  // The sites array + active id, not a derived boolean: a capability edit changes caps but
  // keeps the site count (ADR 120/183).
  const availableSites = useSiteStore((s) => s.availableSites);
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const capabilitiesFor = useSiteStore((s) => s.capabilitiesFor);
  const canAdminOrgUnits = useSiteStore((s) => s.adminOrgUnits.length > 0);
  const caps = capabilitiesFor(activeSiteId);
  return {
    isGlobalAdmin,
    roleRank,
    hasCapability: (cap) => caps[cap],
    hasSite: availableSites.length > 0,
    canAdminOrgUnits,
    isSales,
  };
}
